import { useMemo, useState } from 'react';
import { api } from '../../../core/api/queries';
import { useReportPage } from '../../../shared/hooks';
import { ReportPageLayout } from '../../../shared/components/layouts';
import { EditionSelector, normalizeSchoolName } from '../../../shared/components/tables';
import { columnBuilder } from '../../../shared/utils';
import type { WasteCollectionFormReportDTO } from '../api/waste-collection.api';
import { useAuthProviderV2, useRoutesProvider } from '../../../providers/provider-hooks';
import { OrderFormDialog } from '../components/OrderFormDialog';
import { ActionButton } from '../../../shared/components/buttons/ActionButton';

/**
 * Rejected Waste Orders Report Page
 *
 * Displays waste collection orders that have been rejected (isValidated: false).
 * Accessible by ADMIN, DBEST, and PROFESOR roles.
 *
 * This is a view-only report focused on rejection details.
 */
export function RejectedWasteOrdersReportPage() {
    const { report, adapters, handleExport, editions, currentEdition, changeEdition } = useReportPage({
        queryHook: api.wasteOrders.report,
        initialSort: ['timestamp,desc'],
        exportEndpoint: '/api/waste-collection-forms/report',
        exportFilename: 'raport_comenzi_respinse',
        withEditions: true,
        initialFilter: { isValidated: false }  // Only rejected orders
    });

    const { userInfo } = useAuthProviderV2();
    const { ROLES } = useRoutesProvider();
    const isAdmin = userInfo?.role === ROLES.ADMIN || userInfo?.role === ROLES.DBEST;

    // Comanda respinsa nu se editeaza, dar tabelul arata doar o parte din ea: dialogul o arata toata.
    const [comandaVazuta, setComandaVazuta] = useState<WasteCollectionFormReportDTO | null>(null);

    const columns = useMemo(() =>
        columnBuilder<WasteCollectionFormReportDTO>()
            .action('actions_view', 'Acțiuni', (_, row) => (
                <ActionButton intent="edit" onClick={() => setComandaVazuta(row)} title="Vizualizare comandă">
                    Vizualizare
                </ActionButton>
            ))
            // Rejection details
            .timestamp('timestamp', 'Data plasare comanda')
            .text('rejectReason', 'Motiv respingere', 'Motiv respingere...')
            .timestamp('validatedAt', 'Data respingere')

            // School and contact information
            .nested('school.codSiiirUnitate', 'Cod SIIIR', (row) => row.school?.codSiiirUnitate, {
                filterPlaceholder: 'Cod SIIIR...',
                filterType: 'number'
            })
            // Titlul randului in rezumatul de pe mobil, normalizat doar la afisare.
            .nested('school.numeInstitutieInvatamant', 'Școala', (row) => normalizeSchoolName(row.school?.numeInstitutieInvatamant), {
                cardRole: 'title',
                filterPlaceholder: 'Denumire instituție...'
            })
            .text('contactPersonName', 'Persoana contact', 'Nume persoana contact...')
            .text('contactPersonPhone', 'Telefon', 'Număr telefon...')
            .text('contactPersonEmail', 'Email', 'Adresa email...')

            // Pickup address
            .nested('pickupAddress.judet', 'Județ ridicare', (row) => row.pickupAddress?.judet, {
                filterPlaceholder: 'Județ...'
            })
            .nested('pickupAddress.oras', 'Oras ridicare', (row) => row.pickupAddress?.oras, {
                filterPlaceholder: 'Oras...'
            })
            .nested('pickupAddress.strada', 'Strada ridicare', (row) => row.pickupAddress?.strada, {
                filterPlaceholder: 'Strada...'
            })

            // Weight estimates
            .number('deeeKg', 'Kg DEEE Estimate', 'Kg DEEE...')
            .number('dbaKg', 'Kg DBA Estimate', 'Kg DBA...')
            .number('becuriSiNeoaneKg', 'Kg Becuri si Neoane Estimate', 'Kg...')

            // Photos and documents
            .photo('photo1', 'Foto 1')
            .photo('photo2', 'Foto 2')
            .photo('photo3', 'Foto 3')
            .document('deeeOrderDocument', 'Document Comanda DEEE')
            .document('dbaOrderDocument', 'Document Comanda DBA')

            .build(),
        []
    );

    return (
        <>
        <ReportPageLayout
            title="Raport Comenzi Respinse"
            report={report}
            adapters={adapters}
            handleExport={handleExport}
            columns={columns}
            entityLabel="comenzi respinse"
            extraControls={
                isAdmin && (
                    <EditionSelector
                        editions={editions || []}
                        currentEditionId={currentEdition?.id}
                        onChange={changeEdition || (() => {})}
                        disabled={report.loading}
                    />
                )
            }
        />
        {comandaVazuta && (
            <OrderFormDialog
                isOpen
                onClose={() => setComandaVazuta(null)}
                orderData={comandaVazuta}
                readOnly
            />
        )}
        </>
    );
}
